import React, { useEffect, useRef } from "react";

// A blocking progress dialog. While it is open the backdrop swallows every
// pointer event, the page behind cannot scroll, and Escape does nothing - the
// Cancel button is deliberately the only way out.
const ProcessingModal = ({
  open,
  title = "Processing",
  detail,
  elapsed,
  percent = null, // null renders an indeterminate bar
  onCancel,
}) => {
  const cancelRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const { overflow } = document.body.style;
    document.body.style.overflow = "hidden";
    cancelRef.current?.focus();

    // Escape must not dismiss this, and Tab must not reach the page behind it.
    const onKeyDown = (e) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
      }
      if (e.key === "Tab") {
        e.preventDefault();
        cancelRef.current?.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);
    return () => {
      document.body.style.overflow = overflow;
      document.removeEventListener("keydown", onKeyDown, true);
    };
  }, [open]);

  if (!open) return null;

  const clamped =
    percent === null ? null : Math.min(100, Math.max(0, Math.round(percent)));

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50 px-4"
      role="dialog"
      aria-modal="true"
      aria-labelledby="processing-title"
      // Clicking the backdrop must not dismiss a job that is still running.
      onMouseDown={(e) => e.preventDefault()}
    >
      <div className="w-full max-w-[380px] rounded-lg bg-white p-6 text-center shadow-lg dark:bg-darkblack-600">
        <div className="mb-4 flex justify-center">
          <svg
            className="h-10 w-10 animate-spin text-success-300"
            viewBox="0 0 24 24"
            fill="none"
            aria-hidden="true"
          >
            <circle
              className="opacity-25"
              cx="12"
              cy="12"
              r="10"
              stroke="currentColor"
              strokeWidth="4"
            />
            <path
              className="opacity-75"
              fill="currentColor"
              d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"
            />
          </svg>
        </div>

        <h3
          id="processing-title"
          className="mb-1 text-lg font-bold text-bgray-900 dark:text-white"
        >
          {title}
        </h3>

        {detail && (
          <p className="mb-4 text-sm text-bgray-600 dark:text-bgray-50">{detail}</p>
        )}

        <div
          className="mb-2 h-2 w-full overflow-hidden rounded-full bg-bgray-100 dark:bg-darkblack-500"
          role="progressbar"
          aria-valuenow={clamped === null ? undefined : clamped}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-label={title}
        >
          {clamped === null ? (
            <div className="progress-indeterminate h-full w-1/3 rounded-full bg-success-300" />
          ) : (
            <div
              className="h-full rounded-full bg-success-300 transition-all duration-200"
              style={{ width: `${clamped}%` }}
            />
          )}
        </div>

        <p className="mb-5 text-sm font-medium text-bgray-600 dark:text-bgray-50">
          {clamped === null ? "Working" : `${clamped}%`}
          {elapsed ? ` · ${elapsed}` : ""}
        </p>

        <button
          ref={cancelRef}
          type="button"
          onClick={onCancel}
          className="w-full rounded-lg border border-bgray-300 px-6 py-2.5 text-sm font-semibold text-bgray-700 transition-colors hover:bg-bgray-100 dark:border-darkblack-400 dark:text-bgray-50 dark:hover:bg-darkblack-500"
        >
          Cancel
        </button>
      </div>
    </div>
  );
};

export default ProcessingModal;
